import { createStorefrontApiClient } from '@shopify/storefront-api-client';

const storefrontApiKey = SDG.Data.bopisStorefrontApiKey // eslint-disable-line no-undef
const client = storefrontApiKey !== "" ? createStorefrontApiClient({
  storeDomain: SDG.Data.shopifyHost, // eslint-disable-line no-undef
  apiVersion: "2024-10",
  publicAccessToken: storefrontApiKey
}) : null;

const STORE_AVAILABILITY_QUERY = `
  query GetStoreAvailability($variantId: ID!) {
    node(id: $variantId) {
      ... on ProductVariant {
        storeAvailability(first: 100) {
          edges {
            node {
              available
              location {
                id
                name
                joineryStoreId: metafield(namespace: "locations", key: "joinery_store_id") {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Extract numeric ID from Shopify GID
 * @param {string} gid - Shopify GID (e.g., "gid://shopify/Location/12345")
 * @returns {string|null} - Numeric ID
 */
export const extractIdFromGid = (gid) => {
  if (!gid) return null;
  const parts = gid.split('/');
  return parts[parts.length - 1];
};

/**
 * Fetch store availability from Shopify Storefront API
 * @param {string} variantId - Shopify variant ID (numeric, will be converted to GID)
 * @returns {Promise<Array>} - Array of store availability objects
 */
export const fetchStoreAvailability = async (variantId) => {
  if (!client) {
    throw new Error('Storefront API client not configured');
  }

  const gid = `gid://shopify/ProductVariant/${variantId}`;

  const response = await client.request(STORE_AVAILABILITY_QUERY, {
    variables: { variantId: gid },
  });

  const { data, errors } = response;

  if (errors) {
    console.error('Storefront API errors:', errors);
    throw new Error(errors);
  }

  return data?.node?.storeAvailability?.edges?.map(
    (edge) => edge.node
  ) || [];
};

/**
 * Find a matching store from availability data by Shopify location ID
 * @param {Array} availabilityData - Store availability from Storefront API
 * @param {string} shopifyStoreId - Shopify location ID from Stockist custom_fields
 * @returns {Object|null} - Matching store availability or null
 */
export const findMatchingStore = (availabilityData, shopifyStoreId) => {
  if (!shopifyStoreId || !availabilityData?.length) return null;

  return availabilityData.find((store) => {
    const locationId = extractIdFromGid(store.location?.id);
    return locationId === shopifyStoreId;
  });
};

/**
 * Determine whether store hours are available to compute a pickup estimate.
 * @param {Object|null} pickupData - Pickup availability API response
 * @returns {boolean} - True when hours exist to compute an estimate
 */
export const hasStoreHours = (pickupData) => {
  if (!pickupData) return false;

  return (
    pickupData.availableToday != null ||
    pickupData.timeToCutoff != null ||
    pickupData.nextAvailableDate != null
  );
};

/**
 * Fetch pickup availability from Joinery API
 * @param {string} joineryStoreId - Joinery/Spree store ID
 * @returns {Promise<Object|null>} - Pickup availability data
 */
export const fetchJoineryPickupAvailability = async (joineryStoreId) => {
  if (!joineryStoreId) {
    throw new Error('No Joinery store ID provided');
  }

  const joineryHost = SDG.Data.joineryHost || '';
  const url = `${joineryHost}api/v1/store_pickup_availability?store_id=${joineryStoreId}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Joinery API error: ${response.status}`);
  }
  return response.json();
};

/**
 * Main function to check pickup availability for a variant at a specific store
 * @param {string} variantId - Shopify variant ID
 * @param {string} shopifyStoreId - Shopify location ID from Stockist custom_fields
 * @returns {Promise<Object>} - Combined availability data
 */
export const checkPickupAvailability = async (variantId, shopifyStoreId) => {
  try {
    // Step 1: Get store availability from Shopify
    const availabilityData = await fetchStoreAvailability(variantId);

    // Step 2: Find the matching store by Shopify location ID
    const matchingStore = findMatchingStore(availabilityData, shopifyStoreId);

    if (!matchingStore) {
      return {
        hasStock: false,
        storeAvailability: null,
        pickupAvailability: null,
        error: 'Store not found in availability data',
      };
    }

    // Step 3: Check if in stock
    if (!matchingStore.available) {
      return {
        hasStock: false,
        storeAvailability: matchingStore,
        pickupAvailability: null,
      };
    }

    // Step 4: Get Joinery store ID from metafield
    const joineryStoreId = matchingStore.location?.joineryStoreId?.value;

    if (!joineryStoreId) {
      return {
        hasStock: true,
        storeAvailability: matchingStore,
        pickupAvailability: null,
        error: 'No Joinery store ID configured for this location',
      };
    }

    // Step 5: Fetch pickup availability from Joinery
    const joineryData = await fetchJoineryPickupAvailability(joineryStoreId);

    return {
      hasStock: true,
      storeAvailability: matchingStore,
      pickupAvailability: joineryData,
      joineryStoreId,
    };
  } catch (err) {
    console.error('Error checking pickup availability:', err);
    return {
      hasStock: false,
      storeAvailability: null,
      pickupAvailability: null,
      error: err.message,
    };
  }
};
